import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Calendar, DollarSign, TrendingUp, ArrowRight, Clock, ExternalLink, Loader2, GitBranch, Users, Presentation, CheckCircle, Trophy, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from '@/contexts/RoleContext';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { useStageMapping } from "@/hooks/useStageMapping";
import { useSetupStatus } from "@/hooks/useSetupStatus";
import { SetupWarning } from "@/components/ui/setup-warning";
import { SectionInfo } from "@/components/ui/section-info";
import { isCategoryAtOrDeeper } from "@/lib/stage-categories";
import { PageTour } from "@/components/tour/PageTour";
import { dashboardSteps } from "@/lib/tour-steps";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar
} from "recharts";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export default function Dashboard() {
  const navigate = useNavigate();
  const { clientId } = useRole();
  const [loading, setLoading] = useState(true);
  const { mappings, mapStage, isLoading: mappingsLoading } = useStageMapping(clientId);
  const setupStatus = useSetupStatus(clientId);
  const [funnelEvents, setFunnelEvents] = useState<{ stage: string }[]>([]);
  const [clientData, setClientData] = useState<{
    company_name: string;
    contact_name: string | null;
    package: string;
  } | null>(null);
  const [weekDemos, setWeekDemos] = useState<any[]>([]);
  const [monthDemos, setMonthDemos] = useState<any[]>([]);
  const [recentDemos, setRecentDemos] = useState<any[]>([]);
  const [upcomingDemos, setUpcomingDemos] = useState<any[]>([]);
  const [billingThisWeek, setBillingThisWeek] = useState<number | null>(null);
  const [funnelData, setFunnelData] = useState<{ name: string; value: number; fill: string }[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!clientId) return;
      setLoading(true);

      try {
        const now = new Date();
        const weekStart = format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        const weekEnd = format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');
        const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');
        const today = format(now, 'yyyy-MM-dd');

        // Run all queries in parallel
        const [clientRes, weekRes, monthRes, recentRes, upcomingRes, billingRes, allDemosRes, funnelEventsRes] = await Promise.all([
          supabase.from('clients').select('company_name, contact_name, package').eq('id', clientId).single(),
          supabase.from('demos').select('id, status, outcome').eq('client_id', clientId).gte('demo_date', weekStart).lte('demo_date', weekEnd),
          supabase.from('demos').select('id, status, outcome').eq('client_id', clientId).gte('demo_date', monthStart).lte('demo_date', monthEnd),
          supabase.from('demos').select('id, prospect_name, demo_date, demo_time, status, outcome').eq('client_id', clientId).order('demo_date', { ascending: false }).limit(5),
          supabase.from('demos').select('id, prospect_name, demo_date, demo_time, status').eq('client_id', clientId).gte('demo_date', today).order('demo_date', { ascending: true }).limit(5),
          supabase.from('weekly_billing').select('total_amount_cents, billable_demos').eq('client_id', clientId).order('billing_week_start', { ascending: false }).limit(1),
          supabase.from('demos').select('id, status, outcome').eq('client_id', clientId),
          supabase.from('funnel_events').select('stage').eq('client_id', clientId),
        ]);

        if (clientRes.data) setClientData(clientRes.data);
        if (weekRes.data) setWeekDemos(weekRes.data);
        if (monthRes.data) setMonthDemos(monthRes.data);
        if (recentRes.data) setRecentDemos(recentRes.data);
        if (upcomingRes.data) setUpcomingDemos(upcomingRes.data);
        if (billingRes.data && billingRes.data.length > 0) {
          setBillingThisWeek(billingRes.data[0].total_amount_cents);
        }

        if (funnelEventsRes.data) setFunnelEvents(funnelEventsRes.data);

        // Build funnel data from all demos
        if (allDemosRes.data) {
          const all = allDemosRes.data;
          const totalScheduled = all.length;
          const completed = all.filter((d: any) => d.status === 'completed').length;
          const qualified = all.filter((d: any) => d.outcome === 'closed' || d.outcome === 'follow_up').length;
          const closed = all.filter((d: any) => d.outcome === 'closed').length;
          setFunnelData([
            { name: "Scheduled", value: totalScheduled, fill: "hsl(182, 52%, 58%)" },
            { name: "Completed", value: completed, fill: "hsl(180, 52%, 48%)" },
            { name: "Qualified", value: qualified, fill: "hsl(179, 100%, 18%)" },
            { name: "Closed", value: closed, fill: "hsl(160, 84%, 39%)" },
          ]);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [clientId]);

  const displayName = clientData?.contact_name?.split(' ')[0] || 'there';
  const companyName = clientData?.company_name || 'Your Company';
  const packageType = clientData?.package || 'standard';

  // Compute metric values from real data
  const weekDemoCount = weekDemos.length;
  const weekCompleted = weekDemos.filter(d => d.status === 'completed').length;
  const weekQualified = weekDemos.filter(d => d.outcome === 'closed' || d.outcome === 'follow_up').length;
  const monthDemoCount = monthDemos.length;

  // Pipeline metrics from funnel_events + stage mappings
  const pipelineMetrics = useMemo(() => {
    if (mappings.length === 0 || funnelEvents.length === 0) {
      return { leads: 0, demosBooked: 0, demosCompleted: 0, closedWon: 0 };
    }
    let leads = 0;
    let demosBooked = 0;
    let demosCompleted = 0;
    let closedWon = 0;
    for (const event of funnelEvents) {
      const category = mapStage(event.stage);
      if (category === 'unmapped') continue;
      leads++;
      if (isCategoryAtOrDeeper(category, 'demo_booked')) demosBooked++;
      if (isCategoryAtOrDeeper(category, 'demo_completed')) demosCompleted++;
      if (category === 'closed_won') closedWon++;
    }
    return { leads, demosBooked, demosCompleted, closedWon };
  }, [funnelEvents, mappings, mapStage]);

  const hasMappings = mappings.length > 0;

  if (!clientId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
    <PageTour steps={dashboardSteps} tourId="dashboard" />
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Dashboard Title with Company Name */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{companyName} Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's what's happening with your account.</p>
        </div>
        <BadgeVenture variant={packageType === 'elite' ? 'info' : 'default'}>
          {packageType === 'elite' ? 'Elite Package' : 'Standard Package'}
        </BadgeVenture>
      </div>
      
      {/* Setup Warnings */}
      {!setupStatus.loading && (
        <div className="space-y-2">
          {!setupStatus.ghlConnected && (
            <SetupWarning message="GHL is not connected — pipeline and demo data is unavailable. Contact your Venture Hills rep." />
          )}
          {setupStatus.ghlConnected && !setupStatus.stageMappingsConfigured && (
            <SetupWarning message="Pipeline stages are being configured — pipeline metrics will appear once mapping is complete." />
          )}
          {!setupStatus.googleAdsConnected && !setupStatus.metaAdsConnected && !(setupStatus.googleAdsNotNeeded && setupStatus.metaAdsNotNeeded) && (
            <SetupWarning message="Ad accounts are being set up — campaign data will appear once connected." />
          )}
        </div>
      )}

      {/* Welcome Banner */}
      <motion.div
        className="relative overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary p-6"
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="relative z-10">
          <h2 className="text-xl font-bold text-primary-foreground">Welcome back, {displayName}!</h2>
          <p className="text-primary-foreground/80 mt-1">
            {weekDemoCount > 0
              ? `You have ${weekDemoCount} demo${weekDemoCount === 1 ? '' : 's'} this week`
              : 'No demos scheduled this week'}
          </p>
          <Button 
            variant="ghost-border" 
            className="mt-4 border-white/30 text-white hover:bg-white/10 hover:border-white"
            onClick={() => navigate('/demo-library')}
          >
            Review Demos <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      </motion.div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          {
            icon: Calendar,
            label: "This Week",
            tooltip: "Total demos completed in the current billing week (Sun-Sat)",
            value: String(weekDemoCount),
            subtext: `Showed: ${weekCompleted} | Qualified: ${weekQualified}`,
          },
          {
            icon: DollarSign,
            label: "Billing This Week",
            tooltip: "Estimated charge for the current billing week based on completed demos",
            value: billingThisWeek !== null ? `$${(billingThisWeek / 100).toFixed(0)}` : '$0',
            subtext: billingThisWeek !== null ? "Most recent billing" : "No billing yet",
          },
          {
            icon: TrendingUp,
            label: "This Month",
            tooltip: "Total demos scheduled or completed in the current calendar month",
            value: String(monthDemoCount),
            subtext: `${monthDemoCount} demo${monthDemoCount === 1 ? '' : 's'} this month`,
          },
        ].map((metric, index) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
          >
            <VentureCard className="h-full">
              <VentureCardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <metric.icon className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {metric.label}
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{metric.tooltip}</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </p>
                  <p className="text-3xl font-bold">{loading ? '...' : metric.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{metric.subtext}</p>
                </div>
              </VentureCardContent>
            </VentureCard>
          </motion.div>
        ))}
      </div>

      {/* Demo Funnel */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <VentureCard data-tour-id="demo-performance">
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              Demo Performance
              <TooltipProvider delayDuration={0}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Overview of all demo activity for your account. Data comes from GHL pipeline synced daily.</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </VentureCardTitle>
            <SectionInfo description="Demos scheduled, completed, and closed across all your campaigns." />
          </VentureCardHeader>
          <VentureCardContent>
            {funnelData.length > 0 && funnelData[0].value > 0 ? (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={funnelData} layout="vertical" barSize={40}>
                      <XAxis type="number" stroke="#525252" fontSize={12} />
                      <YAxis dataKey="name" type="category" stroke="#525252" fontSize={12} width={80} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0a0a0a",
                          border: "1px solid #262626",
                          borderRadius: "8px",
                          color: "#fff"
                        }}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <p className="text-2xl font-bold">
                      {funnelData[0].value > 0 ? Math.round((funnelData[1].value / funnelData[0].value) * 100) : 0}%
                    </p>
                    <div className="flex items-center justify-center gap-1">
                      <p className="text-xs text-muted-foreground">Show Rate</p>
                      <TooltipProvider>
                        <UITooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent><p>Percentage of booked demos where the prospect showed up</p></TooltipContent>
                        </UITooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">
                      {funnelData[1].value > 0 ? Math.round((funnelData[2].value / funnelData[1].value) * 100) : 0}%
                    </p>
                    <p className="text-xs text-muted-foreground">Qualified Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">
                      {funnelData[0].value > 0 ? Math.round((funnelData[3].value / funnelData[0].value) * 100) : 0}%
                    </p>
                    <div className="flex items-center justify-center gap-1">
                      <p className="text-xs text-muted-foreground">Close Rate</p>
                      <TooltipProvider>
                        <UITooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent><p>Percentage of completed demos that resulted in a closed deal</p></TooltipContent>
                        </UITooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Calendar className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm">No demo data yet</p>
                <p className="text-xs mt-1">Performance metrics will appear once demos are recorded</p>
              </div>
            )}
          </VentureCardContent>
        </VentureCard>
      </motion.div>

      {/* Activity Feed + Upcoming Demos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <VentureCard className="h-full" data-tour-id="recent-activity">
            <VentureCardHeader>
              <VentureCardTitle>Recent Activity</VentureCardTitle>
              <SectionInfo description="Latest demo activity and status changes." />
            </VentureCardHeader>
            <VentureCardContent className="space-y-2">
              {recentDemos.length > 0 ? recentDemos.map((demo) => (
                <div
                  key={demo.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-surface border-l-2 border-transparent hover:border-l-secondary transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${demo.status === 'completed' ? 'bg-green-500' : demo.status === 'scheduled' ? 'bg-secondary' : 'bg-muted-foreground'}`} />
                    <div>
                      <span className="text-sm font-medium">{demo.prospect_name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {demo.status === 'completed' ? 'Completed' : demo.status === 'no_show' ? 'No Show' : demo.status === 'cancelled' ? 'Cancelled' : 'Scheduled'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(demo.demo_date), 'MMM d')}
                      {demo.demo_time ? `, ${demo.demo_time.slice(0, 5)}` : ''}
                    </span>
                    <button className="p-1 rounded hover:bg-accent" onClick={() => navigate('/demos')}>
                      <ExternalLink className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              )) : (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <p className="text-sm">No recent activity</p>
                  <p className="text-xs mt-1">Demos will appear here as they are scheduled</p>
                </div>
              )}
            </VentureCardContent>
          </VentureCard>
        </motion.div>

        {/* Upcoming Demos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <VentureCard className="h-full" data-tour-id="upcoming-demos">
            <VentureCardHeader>
              <VentureCardTitle>Upcoming Demos</VentureCardTitle>
              <SectionInfo description="Demos scheduled in the next 7 days." />
            </VentureCardHeader>
            <VentureCardContent className="space-y-3">
              {upcomingDemos.length > 0 ? upcomingDemos.map((demo) => (
                <div
                  key={demo.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary transition-colors"
                >
                  <div className="space-y-1">
                    <p className="font-medium">{demo.prospect_name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{format(new Date(demo.demo_date), 'MMM d, yyyy')}</span>
                      {demo.demo_time && (
                        <>
                          <span>at</span>
                          <span>{demo.demo_time.slice(0, 5)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <BadgeVenture variant={demo.status === "scheduled" ? "info" : "default"}>
                    {demo.status === 'scheduled' ? 'Scheduled' : demo.status === 'rescheduled' ? 'Rescheduled' : demo.status}
                  </BadgeVenture>
                </div>
              )) : (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Clock className="w-8 h-8 mb-2 opacity-40" />
                  <p className="text-sm">No upcoming demos</p>
                  <p className="text-xs mt-1">Future demos will appear here</p>
                </div>
              )}
            </VentureCardContent>
          </VentureCard>
        </motion.div>
      </div>

      {/* Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle>Account Summary</VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{loading ? '...' : funnelData[0]?.value ?? 0}</p>
                <div className="flex items-center justify-center gap-1">
                  <p className="text-xs text-muted-foreground">Total Demos</p>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent><p>All demos scheduled for your account</p></TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{loading ? '...' : funnelData[1]?.value ?? 0}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{loading ? '...' : funnelData[2]?.value ?? 0}</p>
                <p className="text-xs text-muted-foreground">Qualified</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{loading ? '...' : funnelData[3]?.value ?? 0}</p>
                <p className="text-xs text-muted-foreground">Closed</p>
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>
      </motion.div>

      {/* Pipeline Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
      >
        <VentureCard data-tour-id="pipeline-overview">
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-secondary" />
              Pipeline Overview (All Time)
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Lead funnel based on GHL stage mappings</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            {mappingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : !hasMappings ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <GitBranch className="w-8 h-8 mb-2 opacity-40" />
                <p className="text-sm">Connect GHL and map pipeline stages to see pipeline metrics.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: Users, label: "Leads", value: pipelineMetrics.leads, color: "text-blue-400" },
                  { icon: Presentation, label: "Demos Booked", value: pipelineMetrics.demosBooked, color: "text-purple-400" },
                  { icon: CheckCircle, label: "Demos Completed", value: pipelineMetrics.demosCompleted, color: "text-teal-400" },
                  { icon: Trophy, label: "Deals Closed", value: pipelineMetrics.closedWon, color: "text-green-400" },
                ].map((metric, index) => (
                  <motion.div
                    key={metric.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 + index * 0.05 }}
                    className="text-center p-4 rounded-lg bg-surface border border-border"
                  >
                    <metric.icon className={`w-5 h-5 mx-auto mb-2 ${metric.color}`} />
                    <p className="text-2xl font-bold">{loading ? '...' : metric.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{metric.label}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </VentureCardContent>
        </VentureCard>
      </motion.div>
    </motion.div>
    </>
  );
}
